import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useToast } from '../../../lib/toast';
import { formatBytes } from '../../../lib/format';
import styles from './Hub.module.css';

interface UploadURLResponse {
  uploadUrl: string;
  objectKey: string;
  expiresAt: string;
}

const MAX_BYTES = 500 * 1024 * 1024;

interface Props {
  draftNum: 1 | 2;
  title: string;
}

export function UploadWidget({ draftNum, title }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState<'pick' | 'preview' | 'uploading' | 'done' | 'error'>('pick');
  const [errMsg, setErrMsg] = useState('');
  const qc = useQueryClient();
  const toast = useToast();

  const upload = useMutation({
    mutationFn: async (f: File) => {
      if (!f.type.startsWith('video/')) {
        throw new Error('Please choose a video file.');
      }
      if (f.size > MAX_BYTES) {
        throw new Error('File is over 500 MB.');
      }
      const { uploadUrl, objectKey } = await api.post<UploadURLResponse>(
        '/api/creator/drafts/upload-url',
        {
          draftNum,
          filename: f.name,
          contentType: f.type,
          sizeBytes: f.size,
        },
      );
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', f.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`Upload failed (HTTP ${xhr.status})`));
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(f);
      });
      return api.post<{ video_status: string }>('/api/creator/drafts', {
        draftNum,
        objectKey,
        contentType: f.type,
        sizeBytes: f.size,
      });
    },
    onMutate: () => {
      setStage('uploading');
      setProgress(0);
      setErrMsg('');
    },
    onSuccess: () => {
      setStage('done');
      qc.invalidateQueries({ queryKey: ['creator', 'me'] });
      toast('Upload complete!');
    },
    onError: (err) => {
      setStage('error');
      setErrMsg(err instanceof Error ? err.message : 'Upload failed.');
    },
  });

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
    setFile(f);
    setStage('preview');
  }

  function reset() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    setStage('pick');
    setProgress(0);
    setErrMsg('');
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <>
      <input
        type="file"
        accept="video/*"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={onPick}
      />
      {stage === 'pick' && (
        <div className={styles.uploadZone} onClick={() => inputRef.current?.click()}>
          <div className={styles.uzIcon}>↑</div>
          <div className={styles.uzTitle}>{title}</div>
          <div className={styles.uzSub}>MP4, MOV, AVI — up to 500 MB</div>
        </div>
      )}
      {stage === 'preview' && file && previewUrl && (
        <div>
          <video className={styles.vsVideo} controls src={previewUrl} style={{ marginBottom: 12 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span className={styles.selName}>
              {file.name} ({formatBytes(file.size)})
            </span>
            <div className={styles.uploadActions}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => file && upload.mutate(file)}
              >
                Submit Draft {draftNum}
              </button>
              <button type="button" className={styles.btnGhost} onClick={reset}>
                Change video
              </button>
            </div>
          </div>
        </div>
      )}
      {stage === 'uploading' && (
        <div style={{ padding: '4px 0' }}>
          <div className={styles.progBar}>
            <div className={styles.progFill} style={{ width: `${progress}%` }} />
          </div>
          <div className={styles.progText}>Uploading… {progress}%</div>
        </div>
      )}
      {stage === 'done' && (
        <div style={{ padding: '4px 0' }}>
          <div className={styles.progBar}>
            <div className={`${styles.progFill} ${styles.progDone}`} style={{ width: `100%` }} />
          </div>
          <div className={styles.progText}>Upload complete!</div>
        </div>
      )}
      {stage === 'error' && (
        <>
          <div className={styles.uploadErr}>{errMsg || 'Upload failed.'}</div>
          <div style={{ marginTop: 10 }}>
            <button type="button" className={styles.btnGhost} onClick={reset}>
              Try again
            </button>
          </div>
        </>
      )}
    </>
  );
}
